import { useEffect, useRef, useState } from "react";
import { Prompt, useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Grid } from "@mui/material";

import "../../App.css";

import MainAction from "components/forms/MainAction/MainAction";
import SelectedTool from "components/tools/SelectedTool/SelectedTool";
import SyncfusionViewer from "components/tools/SyncfusionViewer/SyncfusionViewer";

import { useDialog } from "context/DialogContext";
import { useSnackbar } from "context/SnackbarContext";
import { updateField, checkField, initFieldsWithoutPosRef } from "features/doctypesSlice";

import { useSelector, useDispatch } from "react-redux";
import { RootState } from "services/store";
import { QueryClient, QueryClientProvider } from "react-query";
import api from "services/api";
import { login, logout } from "features/userSlice";
import { jwtDecode } from "jwt-decode";
import { AppStatus, setStatus } from "features/appStateSlice";
import { createWorker  } from "tesseract.js";

import imageSplash from "assets/images/splashScreen.png";
import { IDoctypeField, setDoctypes } from "features/doctypesSlice";
import { calculateRatio } from "services/images";
import dayjs from "dayjs";
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import 'dayjs/locale/it';
import 'dayjs/locale/en';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { usePdfViewer } from "context/PdfViewerContext";
import { setCurrentPage, setOcrData } from "features/appStateSlice";

interface RouteParams {
  ged: string;
}

const EDocIndexWeb = () => {
  const { t } = useTranslation();
  const { ged } = useParams<RouteParams>();
  const status = useSelector((state: RootState) => state.appState.status);
  const userConfig = useSelector((state: RootState) => state.user);
  const selectedDoctypeKey = useSelector((state: RootState) => state.doctypes.selectedDoctypeKey);
  const doctype = useSelector((state: RootState) => state.doctypes.doctypes.find((doc) => doc.Key === selectedDoctypeKey));
  const ocrData = useSelector((state: RootState) => state.appState.ocrData);
  const [isOcrBtnLoading, setIsOcrBtnLoading] = useState<boolean>(false);

  dayjs.extend(customParseFormat);
  dayjs.extend(advancedFormat);

  const { pdfViewer } = usePdfViewer();

  const dispatch = useDispatch();
  const block = useRef<boolean>(false);
  let history = useHistory();

  const { showDialog } = useDialog();
  const { showSnackbar } = useSnackbar();

  const queryClient = new QueryClient();

  const generateOcr = async (field: IDoctypeField|undefined) => {
    setIsOcrBtnLoading(true);
    const docTypeKey: string = "" + selectedDoctypeKey;

    if(field && (!field.InputValue || field?.InputValue == "")) {
      setIsOcrBtnLoading(false);
      return;
    }

    if(pdfViewer && doctype){
      const value = field ? field.InputValue : undefined;
      let images:any[] = [];

      pdfViewer.magnification.fitToWidth();

      if(pdfViewer.zoomValue < 60) {
        showSnackbar({
          message: t("dialog.pleaseZoomInDocument"),
          severity: "warning",
          duration: 3000
        });
        setIsOcrBtnLoading(false);
        return;
      }

      try {
        const response = await api.get("/ocrtemplates/check/" + docTypeKey + (field && value ? "_" + value.trim() : ""));

        if(response.status != 200) {
          setIsOcrBtnLoading(false);
          return;
        }

        dispatch(initFieldsWithoutPosRef());
        dispatch(setCurrentPage(null));

        let newOcrData = structuredClone(ocrData);
        if(newOcrData) {
          newOcrData.selectedPage = undefined;
          newOcrData.lastOcrTemplatePage = pdfViewer.currentPageNumber;
          dispatch(setOcrData(newOcrData));
        }

        const zones = response.data.Data.Zones;

        for(let i = 0; i < zones.length; i++) {
          if(zones[i].Page > 0) {
            let imgIndex = images.findIndex((img: any) => img.page === zones[i].Page);
            let pageNumber = pdfViewer.currentPageNumber - 2 + zones[i].Page;

            if(imgIndex < 0 && (pageNumber + 1) <= pdfViewer.pageCount) {
              const viewerPageSize = pdfViewer.viewerBase.pageSize[pdfViewer.currentPageNumber - 2 + zones[i].Page];
              const baseRatio = viewerPageSize.height / viewerPageSize.width;
              const dataImage = await pdfViewer.exportAsImage(pdfViewer.currentPageNumber - 2 + zones[i].Page, { width: 1000, height: 1000 * baseRatio } as any);
              
              images.push({
                page: zones[i].Page,
                image: dataImage,
                ratio: await calculateRatio(pdfViewer, (pdfViewer.currentPageNumber - 2 + zones[i].Page), dataImage)
              });
            }
          }
        }

        //const worker = await createWorker(['fra', 'eng']);
        const worker = await createWorker(userConfig.ocrLangs);

        const ctrlZone = zones.find((zn: any) => zn.OcrType == "CTRL");
        let ctrlError: boolean = false;

        if(ctrlZone) {
          const ctrlImg = images.find((img:any) => img.page == ctrlZone.Page);
          if(ctrlImg) {
            const ctrlData = await worker.recognize(ctrlImg.image, { 
              rectangle: {
                left: ctrlZone.Left * ctrlImg.ratio,
                top: ctrlZone.Top * ctrlImg.ratio,
                width: ctrlZone.Width * ctrlImg.ratio,
                height: ctrlZone.Height * ctrlImg.ratio
              }
            });

            let ctrlDataText = ctrlData.data.text.replace(/\r\n|\r|\n/g, " ").trim();

            if(ctrlDataText.toLowerCase() != ctrlZone.Value.toLowerCase()) {
              showSnackbar({
                message: t("dialog.ocrTemplateNotRecognized"),
                severity: "warning",
                duration: 3000
              });
              ctrlError = true;
            }
          }
        }

        if(!ctrlError){
          for (let i = 0; i < zones.length; i++) {
            const fieldDest: IDoctypeField|undefined = doctype.Fields.find((fld) => fld.Pos != doctype.PosRef && fld.Pos == zones[i].Pos);

            if(fieldDest && zones[i].OcrType != "CTRL" && zones[i].OcrType != "") {
              let dataText = "";

              if(zones[i].OcrType == "V") {
                dataText = zones[i].Value;
              } else if (zones[i].OcrType == "C") {
                const dataImg = images.find((img:any) => img.page == zones[i].Page);
                if(dataImg) {
                  const data = await worker.recognize(dataImg.image, { 
                    rectangle: {
                      left: zones[i].Left * dataImg.ratio,
                      top: zones[i].Top * dataImg.ratio,
                      width: zones[i].Width * dataImg.ratio,
                      height: zones[i].Height * dataImg.ratio
                    }
                  });

                  dataText = data.data.text.replace(/\r\n|\r|\n/g, " ").trim();
                }
              }

              if(fieldDest.Type == "DATE") {
                let dateValid = false;
                let i = 0;

                if(userConfig.ocrDateFormats.length > 0) {
                  do {
                    let j = 0;

                    do {
                      const ocrLangShort = userConfig.ocrLangs[j].substring(0, 2);

                      let dataDate = dayjs(dataText, userConfig.ocrDateFormats[i], ocrLangShort);
                      dateValid = dataDate.isValid();
                      
                      if(dateValid) {
                        dataText = dataDate.format("DD.MM.YYYY");
                      }

                      j++;
                    } while(!dateValid && j < userConfig.ocrLangs.length);

                    i++;
                  } while(!dateValid && i < userConfig.ocrDateFormats.length);
                }

                if(!dateValid) {
                  dataText = "";
                }
              } else if(fieldDest.Type == "NUMBER") {
                dataText = dataText.replace("'", "").replace(" ", "").replace(",", ".");
                
                let dataTextSplit = dataText.split(".");
                dataTextSplit[0] = dataTextSplit[0].replace(/\D/g, "");
                
                if(dataTextSplit.length >= 2) {
                  dataTextSplit[1] = dataTextSplit[1].replace(/\D/g, "");
                }

                dataText = `${dataTextSplit[0]}${dataTextSplit.length >= 2 ? `.${dataTextSplit[1]}` : ""}`;
              }

              if(!dataText) {
                dataText = "";
              }

              if(fieldDest){
                if (fieldDest.Type === "AUTOCOMPLETE") {
                  const option = fieldDest.Options.find(
                    (option: any) =>
                      option.Value.toLowerCase() === dataText.toLowerCase() ||
                      option.Label.toLowerCase() === dataText.toLowerCase()
                  );
                  if (option) {
                    dispatch(
                      updateField({
                        doctypeKey: docTypeKey,
                        fieldProperty: "Value",
                        fieldKey: fieldDest.Key,
                        value: option,
                      })
                    );
                    dispatch(
                      updateField({
                        doctypeKey: docTypeKey,
                        fieldProperty: "InputValue",
                        fieldKey: fieldDest.Key,
                        value: option.Label,
                      })
                    );
                  } else {
                    dispatch(
                      updateField({
                        doctypeKey: docTypeKey,
                        fieldProperty: "Value",
                        fieldKey: fieldDest.Key,
                        value: dataText,
                      })
                    );
                    dispatch(
                      updateField({
                        doctypeKey: docTypeKey,
                        fieldProperty: "InputValue",
                        fieldKey: fieldDest.Key,
                        value: dataText,
                      })
                    );
                  }
                } else {
                  dispatch(
                    updateField({
                      doctypeKey: docTypeKey,
                      fieldProperty: "Value",
                      fieldKey: fieldDest.Key,
                      value: dataText,
                    })
                  );
                  dispatch(
                    updateField({
                      doctypeKey: docTypeKey,
                      fieldProperty: "InputValue",
                      fieldKey: fieldDest.Key,
                      value: dataText,
                    })
                  );
                }

                dispatch(checkField({ doctypeKey: docTypeKey, fieldKey: fieldDest.Key }));
              }
            }
          }
        }

        worker.terminate();
      } catch (error: any) {
      }
    }
    setIsOcrBtnLoading(false);
  }

  const checkUser = async () => {
    const token = localStorage.getItem("token");
    if (token != null) {
      try {
        await api.get("/auth/check");
        const decodedToken: any = jwtDecode(token);
        dispatch(
          login({
            username: decodedToken.username,
            token: token,
          })
        );
        return true;
      } catch (ex) {
        console.error(ex);
        dispatch(logout());
        showSnackbar({
          message: t("header.authCheckError"),
          severity: "error",
        });
        localStorage.removeItem("token");
        history.push(`/${ged}/login`);
        return false;
      }
    } else {
      history.push(`/${ged}/login`);
    }
    return false;
  };

  const fetchUserConfig = async () => {
    try {
      let response = await api.get("/user-config");
      dispatch(
        login({
          displayBrWithClient: response.data.Data.QRAvecCodeClient,
          clientName: response.data.Data.ClientName,
          hasCresus: response.data.Data.HasCresus,
          cresusOptions: response.data.Data.CresusOptions,
          code: response.data.Data.CodeClient,
          gedLink: response.data.Data.GedLink,
          hasEDataRecon: response.data.Data.EDataRecon,
          ocrLangs: response.data.Data.OCRLangue,
          ocrDateFormats: response.data.Data.OCRFormatDateOrdre,
          mandatoryBarcode: response.data.Data.CBObligatoire,
        })
      );
      response = await api.get("/doctypes");
      dispatch(setDoctypes(response.data.Data));
    } catch (error: any) {
      console.error(error);
      showDialog({
        type: "lock",
        title: t("modal.errorOccurred"),
        message: error.response?.data?.Message || t("internalError"),
        severity: "error",
      });
    }
  };

  const alertUser = async (e: any) => {
    e.preventDefault();
    e.returnValue = "";
  };

  useEffect(() => {
    window.addEventListener("beforeunload", alertUser);
    return () => {
      window.removeEventListener("beforeunload", alertUser);
    };
  }, []);

  useEffect(() => {
    block.current = status === AppStatus.EDITING;
  }, [status]);

  useEffect(() => {
    const checkAndFetch = async () => {
      const connected = await checkUser();
      if (!connected) return;
      await fetchUserConfig();
    };

    dispatch(setStatus(AppStatus.NO_DOCUMENT));

    checkAndFetch();
  }, []);

  return (
    <>
      <Prompt
        when={block.current}
        message={() => {
          return t("promptMessage");
        }}
      />
      <Box height="100%">
        <QueryClientProvider client={queryClient}>
          {/*<PdfViewerProvider>*/}
            <Grid container spacing={0} sx={{ height: "100%" }}>
              <Grid item xs={1}>
                <MainAction generateOcr={generateOcr} isOcrBtnLoading={isOcrBtnLoading} />
              </Grid>
              <Grid item xs={4} lg={4} xl={3} sx={{ maxHeight: "100%" }}>
                <SelectedTool generateOcr={generateOcr} />
              </Grid>
              <Grid
                item
                xs={7}
                lg={7}
                xl={8}
                sx={{
                  backgroundColor: "white",
                  backgroundImage: `url(${imageSplash})`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  backgroundSize: "auto 85%",
                }}
              >
                <SyncfusionViewer />
              </Grid>
            </Grid>
          {/*</PdfViewerProvider>*/}
        </QueryClientProvider>
      </Box>
    </>
  );
};

export default EDocIndexWeb;